import type { Adapter } from './index.h'

import * as appearance from './entities/appearance'
import * as banners from './entities/banners'
import * as catchups from './entities/catchups'
import * as continueWatching from './entities/continue-watching'
import * as externalContent from './entities/external-content'
import * as globalSearch from './entities/global-search'
import * as i18n from './entities/i18n'
import * as liveEvents from './entities/live-events'
import * as notifications from './entities/notifications'
import * as partnerProgram from './entities/partnerProgram'
import * as player from './entities/player'
import * as profiles from './entities/profiles'
import * as session from './entities/session'
import * as showBundles from './entities/showsBundles'
import * as subscriber from './entities/subscriber'
import * as subscriptions from './entities/subscriptions'
import * as tv from './entities/tv'
import * as tvBundles from './entities/tvBundles'
import * as tvshows from './entities/tvshows'
import * as vods from './entities/vods'
import * as vodBundles from './entities/vodsBundles'
import * as events from './events'

export type {
  AccountAccessToken,
  Features,
  ResetPasswordSession,
  Subscriber,
  UpdateSubscriberPasswordData,
  UpdateSubscriberUsernameData,
} from './entities/subscriber'
export type { Session, SignInParams, TOA } from './entities/session'
export type {
  Adapter,
  API,
  ApiEffect,
  ApiError,
  Page,
  PageableInfo,
  PageableInfoNew,
  PageInfo,
  PageInfoCollection,
  PageInfoWithCursor,
  PageWithCursor,
  Slice,
  SliceInfo,
} from './index.h'
export type {
  ContentStore,
  DRM,
  GenericApiResponse,
  LaData,
  MediaUrl,
  SkipSegment,
  SkipSegmentType,
  WrappedData,
} from './interfaces/generic'
export type {
  AppearanceBanner,
  AppearanceColorStyle,
  AppearanceComponent,
  AppearanceComponentContent,
  AppearanceImageStyle,
  AppearanceLinearGradientStyle,
  AppearanceModule,
  AppearanceModulesSet,
  AppearanceRow,
  AppearanceRowStyle,
  AppearanceStyle,
  AppearanceStylesSet,
  AppearanceUnsetStyle,
} from './entities/appearance'
export type {
  Banner,
  BannerCarousel,
  BannerContentItem,
  BannerContentItemPrice,
  BannerUpdateObj,
  BannerWithPrice,
  CarouselItem,
  CarouselItemTyped,
  FeaturedCarousel,
  FeaturedCarouselTyped,
} from './entities/banners'
export type {
  BundleInfo,
  TvBundleInfo,
  TvBundleWithContent,
  TvShowBundleInfo,
  VodBundleInfo,
  VodBundleWithContent,
} from './interfaces/bundles'
export type {
  Catchup,
  CatchupProgram,
  CatchupProgramsFormatedToObjectWithDatelist,
  CatchupProgramsWithDates,
  CatchupsDateList,
} from './interfaces/catchups'
export type {
  CategoriesWithPageChannelsEpg,
  CategoryWithChannels,
  CategoryWithChannelsApi,
  EpgInfo,
  EpgInfoDto,
  GetAllTvChannelsByPageParams,
  GetPurchasedTvChannelsParams,
  LiveEpgInfo,
  PlaylistChannel,
  TvCategory,
  TvChannel,
  TvChannelUpdated,
  TvChannelUpdateObj,
  TvChannelWithEpg,
  TvEpg,
  TvEpgApiDto,
  TvPlaylist,
} from './interfaces/tv'
export type {
  CategoryWithShows,
  CategoryWithShowsApi,
  CurrentTvShow,
  PlaylistSeason,
  SeriesPlaylist,
  TvShow,
  TvShowCategory,
  TvShowEpisode,
  TvShowRecommended,
  TvShowSeason,
  TvShowUpdated,
  TvShowUpdateObj,
} from './interfaces/tvshows'
export type {
  CategoryWithVods,
  CategoryWithVodsApi,
  Vod,
  VodCategory,
  VodRecommended,
  VodSpecialCategoryWithVodsRecommended,
  VodUpdated,
  VodUpdateObj,
} from './interfaces/vods'
export type { CurrentSubscription } from './interfaces/subscriptions'
export type { FormattedPriceSettings } from './interfaces/priceSettings'
export type { HttpRequestInfo, HttpResponseInfo } from './events/http'
export type {
  GetPartnerProductParams,
  GetPartnerProductsByPageParams,
  PartnerProduct,
  PartnerProductDetails,
} from './interfaces/partnerProgram'
export type {
  EpgProgramme,
  HybridSearchPayload,
  HybridSearchSectionFields,
  SearchHint,
  SearchSection,
} from './entities/global-search/index.h'
export type {
  IContinueWatchingCard,
  IContinueWatchingCardEpisode,
} from './interfaces/continue-watching'
export type {
  IGenerateSeriesPlaylistParams,
  SeriesPlaylistRecalculate,
  TVShowsPlaylist,
  TvShowSeasonWithEpisodes,
} from './interfaces/tvshows'
export type {
  IKidsProfileType,
  IProfile,
  IProfileSummary,
  IProfileSummaryMeta,
} from './interfaces/profiles'
export type { INoraTranslationsFormat } from './interfaces/i18n'
export type { IPlaylist, PlayerUrlError } from './interfaces/player'
export type { LiveEvent } from './interfaces/live-events'
export type { LogicError } from './events/logic'
export type {
  MotnMovie,
  MotnSearchResult,
  MotnSeries,
  MotnServiceLogo,
  MotnStreamingAccessInfo,
  MotnStreamingOption,
} from './entities/external-content/index.h'
export type { PurchaseInfo } from './interfaces/purchaseInfo'
export type { ShowBundleWithContent } from './entities/showsBundles'
export type { TimeRange } from './interfaces/epg'

export { AvatarName, MAX_NUMBER_OF_PROFILES } from './interfaces/profiles'
export { CarouselTypes } from './entities/banners'
export { EventStatus } from './interfaces/live-events'
export {
  GlobalSearchMediaTypes,
  LiveEventsTypes,
  MediaOrderType,
  MediaResolution,
  MediaTypes,
  ResourceTypes,
  VideoType,
} from './interfaces/generic'
export { MIN_PROGRAM_LENGTH_MS } from './constants/tv'
export {
  PasswordPolicyName,
  SubscriberGender,
  SubscriberRegistrationStatus,
} from './entities/subscriber'
export {
  PriceSettingsAvailabilityPeriodTimeUnit,
  PriceSettingsFormattedType,
  PriceSettingsPaymentMethod,
  PriceSettingsType,
} from './interfaces/priceSettings'
export { PurchaseInfoContentPurchaseType } from './interfaces/purchaseInfo'
export {
  RECOMMENDED_AMOUNT_SHOWS,
  RECOMMENDED_AMOUNT_SHOWS_AND_CURRENT,
} from './constants/tvshows'
export {
  RECOMMENDED_AMOUNT_VODS,
  RECOMMENDED_AMOUNT_VODS_AND_CURRENT,
} from './constants/vods'
export { ScheduleType } from './interfaces/catchups'

/**
 * Singleton with API layer,
 * provides events/effects/stores to communicate with backend
 */
export const api = {
  use(adapter: Adapter) {
    adapter(this)
  },

  events,

  appearance,
  banners,
  liveEvents,
  session,
  subscriber,
  tv,
  tvBundles,
  vodBundles,
  showBundles,
  tvshows,
  vods,
  player,
  globalSearch,
  catchups,
  partnerProgram,
  continueWatching,
  notifications,
  profiles,
  i18n,
  subscriptions,
  externalContent,
} as const
